import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import MetricsInspectionContext from '../../../state/context';
import usePayload from './usePayload';
import { getMetricsV3 } from 'common/services/metrics.service';
import { get } from 'lodash';
import { mapActivityTypeToEntityKey } from './entities.utils';
import useEntitiesAttributes from './useEntitiesAttributes';
import {
  setBranchesCycleTimeStages,
  setIsLoadingScatter,
  setScatterEntitiesIds,
  setMetricRawEvents
} from '../../../state/actions';
import { metricNames } from '@linearb/linjs-common';

const DEBOUNCE_TIMEOUT = 500;

const useFetchData = () => {
  const {
    state: {
      widget,
      metricName,
      filters: { activityType },
      periods
    },
    dispatch
  } = useContext(MetricsInspectionContext);
  const isInitialFetch = useRef(false);
  const payload = usePayload();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const scatterEntitiesIds = useMemo(
    () =>
      data
        ?.map(entity => entity[mapActivityTypeToEntityKey[activityType]])
        .filter(Boolean),
    [data]
  );

  const mapEntityIdToAttributes = useEntitiesAttributes();
  const isEntitiesNamesLoading = mapEntityIdToAttributes.isLoading;

  const dataWithNames = useMemo(
    () =>
      data.map(item => {
        const attributes =
          mapEntityIdToAttributes[
            item[mapActivityTypeToEntityKey[activityType]]
          ];
        return {
          ...item,
          ...attributes
        };
      }),
    [mapEntityIdToAttributes]
  );

  const trackingInitialFetch = data => {
    if (!isInitialFetch.current) {
      TrackingService.logEvent(TRACKING.EVENTS.MODAL_OPENED, {
        page: TRACKING.PAGES.METRICS_INSPECTION,
        widget_name: widget?.name,
        data_points_count: data?.length,
        dates_before: periods?.[0]?.before,
        dates_after: periods?.[0]?.after
      });

      isInitialFetch.current = true;
    }
  };

  const fetchData = useDebounceCallback(async () => {
    try {
      dispatch(setIsLoadingScatter(true));

      const res = await getMetricsV3(payload.filters);
      //const res = scatterMock;
      const metricData = get(res, `[0].data[0]['${metricName}:raw']`, []);
      if (metricName === metricNames.CYCLE_TIME && metricData?.length) {
        const branchesCycleTimeStages = metricData.reduce((acc, current) => {
          acc[current.branch_id] = current.metric_names;
          return acc;
        }, {});
        dispatch(setBranchesCycleTimeStages(branchesCycleTimeStages));
      }
      setData(metricData?.length > 0 ? metricData : []);
      dispatch(setMetricRawEvents(metricData));
      trackingInitialFetch(metricData);
    } catch (e) {
    } finally {
      dispatch(setIsLoadingScatter(false));
      setIsLoading(false);
    }
  }, DEBOUNCE_TIMEOUT);

  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [...payload.dependencies]);

  useEffect(() => {
    dispatch(setScatterEntitiesIds(scatterEntitiesIds));
  }, [scatterEntitiesIds]);

  return {
    data: dataWithNames,
    isLoading: isLoading || isEntitiesNamesLoading
  };
};

export default useFetchData;
